/**
 * Doctor schedule: day view, slot grid, manual booking, status and deposit actions.
 *
 * This one page replaces the single largest cluster of legacy code — roughly
 * 9,000 lines of doctor_dashboard.js covering `renderScheduleManagement`,
 * `renderGlobalCalendar` (defined twice), `viewDayDetails` (twice),
 * `openDayModal`, `openManualBookingModal` (twice), `saveManualBooking` (twice),
 * `handleSaveManualBooking` (twice), `showBookingActions`, `moveBooking`,
 * `confirmMoveBooking` (twice), `confirmDepositPayment` (three times),
 * `rejectDepositPayment`, `markBookingComplete`, `cancelBooking` and the whole
 * HTML5 drag-and-drop layer (`handleBookingDragStart` … `fillGapAfterMove`).
 *
 * The drag-to-reschedule interaction is preserved as an explicit "move" action
 * rather than raw drag events: it was the source of most of the duplicated
 * logic, and a dropdown is both testable and reachable by keyboard.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  SectionTitle,
  Select,
  Spinner,
  Textarea,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { useNavigate } from 'react-router-dom';
import { useDoctorBookings, useBookingActions } from '../../hooks/useBookings';
import { bookingApi, clinicApi } from '../../api/endpoints';
import {
  BOOKING_STATUS,
  DEPOSIT_STATE_LABELS,
  buildBookingPayload,
  depositState,
  statusLabel,
  statusStyle,
  typeLabel,
} from '../../domain/bookings';
import { buildSlotAvailability, isClinicWorkingDay } from '../../domain/slots';
import { isOfferValidForDate } from '../../domain/offers';
import { BOOKING_TYPES, quoteBooking, formatCurrency } from '../../domain/pricing';
import { addDays, arabicDayOf, formatTime12, isoToDisplayDate, todayKey } from '../../domain/dates';
import { DoctorCalendar } from './calendar/DoctorCalendar';
import { DayDetailsModal } from './calendar/DayDetailsModal';
import { ExtraHoursModal } from './calendar/ExtraHoursModal';
import { useDayOverrides } from './calendar/useDayOverrides';

const DATE_STRIP_DAYS = 21;

/**
 * @param {object}   props
 * @param {Array}    [props.clinics]        Clinics supplied by a host page.
 * @param {boolean}  [props.clinicsLoading] Whether that host is still loading them.
 * @param {boolean}  [props.embedded]       Render without the page header.
 *
 * The clinics list is a prop rather than always a fetch because this screen now
 * lives inside the clinics page, which already holds that list and mutates it.
 * Fetching it twice would mean an edit shows in the cards above and not in the
 * calendar below until a reload.
 */
export function DoctorSchedule({
  clinics: providedClinics = null,
  clinicsLoading = false,
  embedded = false,
} = {}) {
  const { doctorId, doctorProfile } = useAuth();
  const { findPatient } = useData();
  const { success, error: toastError } = useNotifications();

  const navigate = useNavigate();

  const [dateKey, setDateKey] = useState(todayKey());
  const [clinicId, setClinicId] = useState(null);

  // The month grid is the screen the practice actually works from, so it opens
  // first; the day list stays one click away for working through a single day.
  const [view, setView] = useState('calendar');
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [modalDate, setModalDate] = useState(null);
  const [extraHoursDate, setExtraHoursDate] = useState(null);
  const [cancelDayTarget, setCancelDayTarget] = useState(null);

  const clinicsState = useAsync(() => clinicApi.listByDoctor(doctorId), [doctorId], {
    initialData: [],
    enabled: Boolean(doctorId) && !providedClinics,
  });

  const clinics = useMemo(
    () => providedClinics || clinicsState.data || [],
    [providedClinics, clinicsState.data],
  );

  const loadingClinics = providedClinics ? clinicsLoading : clinicsState.loading;

  const clinic = useMemo(
    () => clinics.find((c) => String(c.id) === String(clinicId)) || clinics[0] || null,
    [clinics, clinicId],
  );

  const {
    bookings,
    loading,
    error,
    reload,
    setData,
  } = useDoctorBookings(doctorId, { dateKey });

  const actions = useBookingActions({ bookings, setBookings: setData });

  /* --- month view data --------------------------------------------------- */

  // The backend's GET /bookings has no date-range filter, so the month grid
  // reads the doctor's whole history once and slices it locally — the same
  // thing the legacy calendar did with its global `window.bookings`.
  const allBookingsState = useAsync(() => bookingApi.list({ doctorId }), [doctorId], {
    initialData: [],
    enabled: Boolean(doctorId) && view === 'calendar',
  });

  const monthPrefix = useMemo(
    () => `${visibleMonth.year}-${String(visibleMonth.month + 1).padStart(2, '0')}`,
    [visibleMonth],
  );

  const monthBookings = useMemo(
    () => (allBookingsState.data || []).filter((b) => String(b.date || '').startsWith(monthPrefix)),
    [allBookingsState.data, monthPrefix],
  );

  const offers = useMemo(() => doctorProfile?.offers || [], [doctorProfile]);

  const { extraHours, cancelledDays, saveExtraHours, cancelDay, reopenDay } =
    useDayOverrides(doctorId);

  /** Bookings for the day the modal is showing, from whichever list is loaded. */
  const modalBookings = useMemo(() => {
    if (!modalDate) return [];
    const source = allBookingsState.data?.length ? allBookingsState.data : bookings;
    return source.filter((b) => b.date === modalDate);
  }, [modalDate, allBookingsState.data, bookings]);

  const navigateMonth = (delta) => {
    setVisibleMonth(({ year, month }) => {
      const next = new Date(year, month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  };

  const [manualOpen, setManualOpen] = useState(false);
  const [manualSlot, setManualSlot] = useState(null);
  const [actionTarget, setActionTarget] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const dates = useMemo(
    () => Array.from({ length: DATE_STRIP_DAYS }, (_, i) => addDays(todayKey(), i - 3)),
    [],
  );

  /** Bookings for the visible clinic, indexed by slot time. */
  const bookingsBySlot = useMemo(() => {
    const map = new Map();
    for (const booking of bookings) {
      if (clinic && String(booking.clinicId) !== String(clinic.id)) continue;
      if (!map.has(booking.time)) map.set(booking.time, []);
      map.get(booking.time).push(booking);
    }
    return map;
  }, [bookings, clinic]);

  const slots = useMemo(
    () => buildSlotAvailability(clinic, dateKey, bookings),
    [clinic, dateKey, bookings],
  );

  const dayTotals = useMemo(() => {
    const live = bookings.filter((b) => b.status !== BOOKING_STATUS.CANCELLED);
    return {
      total: live.length,
      completed: live.filter((b) => b.status === BOOKING_STATUS.COMPLETED).length,
      pending: live.filter((b) => b.status === BOOKING_STATUS.PENDING).length,
      revenue: live
        .filter((b) => b.status === BOOKING_STATUS.COMPLETED)
        .reduce((sum, b) => sum + (Number(b.finalPrice) || 0), 0),
    };
  }, [bookings]);

  /* --- actions ---------------------------------------------------------- */

  const runAction = async (fn, successMessage) => {
    try {
      await fn();
      success(successMessage);
      setActionTarget(null);
    } catch (err) {
      toastError(err.message || 'تعذر تنفيذ العملية');
    }
  };

  const handleMove = async (newTime) => {
    if (!moveTarget) return;
    try {
      await actions.reschedule(moveTarget.id, dateKey, newTime);
      success(`تم نقل الحجز إلى ${formatTime12(newTime)}`);
      setMoveTarget(null);
      setActionTarget(null);
      reload();
    } catch (err) {
      toastError(err.message || 'تعذر نقل الحجز');
    }
  };

  const handleRejectDeposit = async (reason) => {
    if (!rejectTarget) return;
    try {
      await actions.rejectDeposit(rejectTarget.id, reason);
      success('تم رفض الإيصال وإبلاغ المريض');
      setRejectTarget(null);
      setActionTarget(null);
    } catch (err) {
      toastError(err.message || 'تعذر رفض الإيصال');
    }
  };

  /** Drag-drop inside the day modal: move one booking to another slot. */
  const handleMoveFromModal = async (bookingId, newTime, newClinicId) => {
    try {
      await actions.reschedule(bookingId, modalDate, newTime, newClinicId);
      success(`تم نقل الحجز إلى ${formatTime12(newTime)}`);
      reload();
      allBookingsState.reload();
    } catch (err) {
      toastError(err.message || 'تعذر نقل الحجز');
    }
  };

  /**
   * "إلغاء اليوم": close the day and cancel everything booked on it.
   *
   * One request. The server closes the day and cancels its bookings in a single
   * transaction, so the day can never end up shut with live bookings still on
   * it — which is what could happen when the frontend fired the cancellations
   * one at a time and a later one failed.
   *
   * The legacy version promised to move those patients to a waiting list. There
   * was no waiting list; they were simply cancelled. That is still what happens,
   * and the confirmation says so rather than a toast saying it afterwards.
   */
  const handleCancelDay = async () => {
    const target = cancelDayTarget;
    if (!target) return;
    try {
      const cancelledCount = await cancelDay(target);
      success(cancelledCount ? `تم إغلاق اليوم وإلغاء ${cancelledCount} حجز` : 'تم إغلاق اليوم');
      reload();
      allBookingsState.reload();
    } catch (err) {
      toastError(err.message || 'تعذر إلغاء اليوم');
    } finally {
      setCancelDayTarget(null);
      setModalDate(null);
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await actions.cancel(cancelTarget.id);
      success('تم إلغاء الحجز');
    } catch (err) {
      toastError(err.message || 'تعذر إلغاء الحجز');
    } finally {
      setCancelTarget(null);
      setActionTarget(null);
    }
  };

  /* --- render ----------------------------------------------------------- */

  return (
    <div className="space-y-6">
      {/* The controls are the same either way; only the chrome around them
          differs. Embedded, this sits under the clinic cards as one more
          section of the clinics page — which is where the legacy build kept
          the calendar. */}
      {(() => {
        const controls = (
          <>
            <Button
              variant={view === 'calendar' ? 'primary' : 'ghost'}
              icon="calendar-days"
              onClick={() => setView('calendar')}
            >
              التقويم
            </Button>
            <Button
              variant={view === 'day' ? 'primary' : 'ghost'}
              icon="list"
              onClick={() => setView('day')}
            >
              اليوم
            </Button>
            <Button variant="ghost" icon="refresh-cw" onClick={reload} loading={loading}>
              تحديث
            </Button>
            <Button
              icon="user-plus"
              disabled={!clinic}
              onClick={() => {
                setManualSlot(null);
                setManualOpen(true);
              }}
            >
              حجز يدوي
            </Button>
          </>
        );

        if (!embedded) {
          return (
            <PageHeader
              title="إدارة المواعيد"
              subtitle={`${arabicDayOf(dateKey)} — ${isoToDisplayDate(dateKey)}`}
              icon="calendar-days"
              actions={controls}
            />
          );
        }

        return (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-6">
            <div className="flex items-center gap-2.5">
              <span className="rounded-xl bg-cyan-500/10 p-2.5 text-cyan-300">
                <Icon name="calendar-days" size={18} />
              </span>
              <div>
                <h2 className="text-base font-bold text-white">جدول مواعيد العيادات</h2>
                <p className="text-xs text-slate-400">
                  {arabicDayOf(dateKey)} — {isoToDisplayDate(dateKey)}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">{controls}</div>
          </div>
        );
      })()}

      {view === 'calendar' && (
        <>
          {allBookingsState.loading && monthBookings.length === 0 ? (
            <Spinner label="جاري تحميل التقويم..." />
          ) : (
            <DoctorCalendar
              year={visibleMonth.year}
              month={visibleMonth.month}
              clinics={clinics}
              bookings={monthBookings}
              offers={offers}
              extraHours={extraHours}
              cancelledDays={cancelledDays}
              onSelectDay={setModalDate}
              onNavigateMonth={navigateMonth}
            />
          )}
        </>
      )}

      {view === 'day' && (
      <>
      {/* Clinic + date */}
      <Panel>
        <div className="space-y-4">
          {clinics.length > 1 && (
            <Field label="العيادة">
              <Select
                value={clinic?.id ?? ''}
                onChange={(event) => setClinicId(event.target.value)}
                className="sm:w-72"
              >
                {clinics.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <div className="custom-scrollbar flex gap-2 overflow-x-auto pb-2">
            {dates.map((date) => {
              const isSelected = date === dateKey;
              const isWorking = clinic ? isClinicWorkingDay(clinic, date) : true;
              const isToday = date === todayKey();
              return (
                <button
                  key={date}
                  type="button"
                  onClick={() => setDateKey(date)}
                  className={`flex w-[74px] shrink-0 flex-col items-center gap-0.5 rounded-xl border p-2.5 transition-colors ${
                    isSelected
                      ? 'border-cyan-500 bg-cyan-500/15 text-cyan-200'
                      : isWorking
                        ? 'border-white/10 bg-slate-900 text-slate-300 hover:bg-slate-800'
                        : 'border-white/5 bg-slate-950 text-slate-600'
                  }`}
                >
                  <span className="text-[10px]">{arabicDayOf(date)}</span>
                  <span className="text-lg font-bold leading-none">{date.slice(-2)}</span>
                  {isToday && <span className="text-[9px] text-cyan-400">اليوم</span>}
                </button>
              );
            })}
          </div>
        </div>
      </Panel>

      {/* Day totals */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <MiniStat icon="calendar-check-2" label="حجوزات" value={dayTotals.total} accent="cyan" />
        <MiniStat icon="check-circle-2" label="مكتملة" value={dayTotals.completed} accent="emerald" />
        <MiniStat icon="clock" label="قيد الانتظار" value={dayTotals.pending} accent="amber" />
        <MiniStat
          icon="banknote"
          label="إيراد اليوم"
          value={formatCurrency(dayTotals.revenue)}
          accent="purple"
        />
      </div>

      {/* Slot grid */}
      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : loadingClinics ? (
        <Spinner label="جاري تحميل العيادات..." />
      ) : !clinic ? (
        <Panel>
          <EmptyState
            icon="building"
            title="لا توجد عيادات"
            message="أضف عيادتك الأولى من الأعلى لتتمكن من استقبال الحجوزات."
          />
        </Panel>
      ) : slots.length === 0 ? (
        <Panel>
          <EmptyState
            icon="calendar-x"
            title="العيادة مغلقة في هذا اليوم"
            message="اختر يوماً آخر، أو عدّل أيام العمل من إعدادات العيادة."
          />
        </Panel>
      ) : (
        <Panel>
          <SectionTitle icon="clock">جدول اليوم</SectionTitle>

          {loading && bookings.length === 0 ? (
            <Spinner variant="inline" />
          ) : (
            <div className="space-y-2">
              {slots.map((slot) => {
                const slotBookings = (bookingsBySlot.get(slot.time) || []).filter(
                  (b) => b.status !== BOOKING_STATUS.CANCELLED,
                );

                return (
                  <div
                    key={slot.time}
                    className="flex flex-wrap items-center gap-3 rounded-xl border border-white/5 bg-white/5 p-3"
                  >
                    <span
                      className={`shrink-0 rounded-lg px-2.5 py-1.5 font-mono text-xs ${
                        slot.isPast ? 'bg-slate-800 text-slate-500' : 'bg-slate-800 text-cyan-300'
                      }`}
                    >
                      {formatTime12(slot.time)}
                    </span>

                    {slotBookings.length === 0 ? (
                      <>
                        <span className="flex-1 text-xs text-slate-500">
                          {slot.isPast ? 'انتهى' : 'متاح'}
                        </span>
                        {!slot.isPast && (
                          <Button
                            variant="subtle"
                            icon="plus"
                            onClick={() => {
                              setManualSlot(slot.time);
                              setManualOpen(true);
                            }}
                          >
                            حجز
                          </Button>
                        )}
                      </>
                    ) : (
                      <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                        {slotBookings.map((booking) => (
                          <button
                            key={booking.id}
                            type="button"
                            onClick={() => setActionTarget(booking)}
                            className="flex min-w-[200px] flex-1 items-center gap-2 rounded-lg border border-white/10 bg-slate-900 p-2 text-right transition-colors hover:border-cyan-500/50"
                          >
                            <Avatar name={booking.patientName} size={30} />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-xs font-bold text-white">
                                {booking.patientName}
                              </span>
                              <span className="block truncate text-[10px] text-slate-400">
                                {typeLabel(booking.type)} — {formatCurrency(booking.finalPrice)}
                              </span>
                            </span>
                            <Badge className={statusStyle(booking.status)}>
                              {statusLabel(booking.status)}
                            </Badge>
                          </button>
                        ))}
                      </div>
                    )}

                    {slotBookings.length > 0 && slot.capacity > slotBookings.length && !slot.isPast && (
                      <Button
                        variant="subtle"
                        icon="plus"
                        onClick={() => {
                          setManualSlot(slot.time);
                          setManualOpen(true);
                        }}
                      >
                        إضافة
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      )}

      </>
      )}

      {/* Day details — the legacy day modal */}
      <DayDetailsModal
        open={Boolean(modalDate)}
        dateKey={modalDate}
        clinics={clinics}
        bookings={modalBookings}
        offers={offers}
        extraHours={extraHours}
        hasOfferForDay={offers.some((o) => isOfferValidForDate(o, modalDate))}
        onClose={() => setModalDate(null)}
        onSelectBooking={(booking) => {
          setModalDate(null);
          setDateKey(booking.date);
          setActionTarget(booking);
        }}
        onBookSlot={(date, time, slotClinic) => {
          setModalDate(null);
          setDateKey(date);
          setClinicId(slotClinic.id);
          setManualSlot(time);
          setManualOpen(true);
        }}
        onConfigureExtraHours={setExtraHoursDate}
        onCreateOffer={() => navigate('/doctor/offers')}
        onCancelDay={setCancelDayTarget}
        isCancelled={cancelledDays.includes(modalDate)}
        onReopenDay={async (date) => {
          try {
            await reopenDay(date);
            success('تم إعادة فتح اليوم');
            setModalDate(null);
          } catch (err) {
            toastError(err.message || 'تعذر إعادة فتح اليوم');
          }
        }}
        onMoveBooking={handleMoveFromModal}
      />

      <ExtraHoursModal
        open={Boolean(extraHoursDate)}
        dateKey={extraHoursDate}
        clinics={clinics}
        existing={extraHours}
        onClose={() => setExtraHoursDate(null)}
        onSave={async (payload) => {
          try {
            await saveExtraHours(payload);
            success(payload.slots?.length ? 'تم حفظ الساعات الإضافية' : 'تم إزالة الساعات الإضافية');
          } catch (err) {
            toastError(err.message || 'تعذر حفظ الساعات الإضافية');
          }
        }}
      />

      <ConfirmDialog
        open={Boolean(cancelDayTarget)}
        onClose={() => setCancelDayTarget(null)}
        onConfirm={handleCancelDay}
        destructive
        title="إلغاء اليوم"
        confirmLabel="نعم، ألغِ اليوم"
        message={
          cancelDayTarget
            ? `سيتم إغلاق ${isoToDisplayDate(cancelDayTarget)} وإلغاء ${
                modalBookings.filter((b) => b.status !== BOOKING_STATUS.CANCELLED).length
              } حجز عليه.

المرضى لن يُنقلوا لموعد آخر تلقائياً — تواصل معهم بنفسك. إعادة فتح اليوم لاحقاً لا تُرجع الحجوزات الملغاة.`
            : ''
        }
      />

      {/* Booking actions */}
      <BookingActionsModal
        booking={actionTarget}
        busy={String(actions.pendingId) === String(actionTarget?.id)}
        onClose={() => setActionTarget(null)}
        onConfirm={() => runAction(() => actions.confirm(actionTarget.id), 'تم تأكيد الحجز')}
        onComplete={() => runAction(() => actions.complete(actionTarget.id), 'تم إنهاء الكشف')}
        onCancel={() => setCancelTarget(actionTarget)}
        onMove={() => setMoveTarget(actionTarget)}
        onConfirmDeposit={() =>
          runAction(() => actions.confirmDeposit(actionTarget.id), 'تم تأكيد العربون')
        }
        onRejectDeposit={() => setRejectTarget(actionTarget)}
      />

      {/* Move to another slot */}
      <MoveBookingModal
        booking={moveTarget}
        slots={slots}
        onClose={() => setMoveTarget(null)}
        onMove={handleMove}
      />

      {/* Reject deposit */}
      <RejectDepositModal
        booking={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onSubmit={handleRejectDeposit}
      />

      {/* Manual booking */}
      <ManualBookingModal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        doctor={doctorProfile}
        clinic={clinic}
        dateKey={dateKey}
        slots={slots}
        presetSlot={manualSlot}
        findPatient={findPatient}
        onCreated={() => {
          setManualOpen(false);
          reload();
          success('تم إنشاء الحجز');
        }}
        onError={(message) => toastError(message)}
      />

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        destructive
        title="إلغاء الحجز"
        confirmLabel="نعم، ألغِ"
        message={
          cancelTarget
            ? `سيتم إلغاء حجز ${cancelTarget.patientName} الساعة ${formatTime12(cancelTarget.time)}.${
                cancelTarget.depositPaid ? '\n\nتنبيه: هذا الحجز عليه عربون مدفوع.' : ''
              }`
            : ''
        }
      />
    </div>
  );
}

/* ========================================================================== */

function MiniStat({ icon, label, value, accent }) {
  const tones = {
    cyan: 'text-cyan-400 bg-cyan-500/10',
    emerald: 'text-emerald-400 bg-emerald-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
  };
  return (
    <div className="glass-panel flex items-center gap-3 rounded-xl p-3">
      <span className={`rounded-lg p-2 ${tones[accent]}`}>
        <Icon name={icon} size={16} />
      </span>
      <div className="min-w-0">
        <p className="truncate text-sm font-bold text-white">{value}</p>
        <p className="truncate text-[11px] text-slate-400">{label}</p>
      </div>
    </div>
  );
}

/* ========================================================================== */

function BookingActionsModal({
  booking,
  busy,
  onClose,
  onConfirm,
  onComplete,
  onCancel,
  onMove,
  onConfirmDeposit,
  onRejectDeposit,
}) {
  const [showReceipt, setShowReceipt] = useState(false);

  if (!booking) return null;

  const deposit = depositState(booking);

  return (
    <Modal open onClose={onClose} title="إجراءات الحجز" icon="settings-2" size="md">
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <Avatar name={booking.patientName} size={48} />
          <div className="min-w-0">
            <h3 className="truncate text-base font-bold text-white">{booking.patientName}</h3>
            <p className="text-xs text-slate-400" dir="ltr">
              {booking.patientPhone || '—'}
            </p>
          </div>
          <Badge className={`mr-auto ${statusStyle(booking.status)}`}>
            {statusLabel(booking.status)}
          </Badge>
        </div>

        <dl className="grid grid-cols-2 gap-3 rounded-xl border border-white/5 bg-white/5 p-3 text-sm">
          <Detail label="الموعد" value={`${isoToDisplayDate(booking.date)} — ${formatTime12(booking.time)}`} />
          <Detail label="النوع" value={typeLabel(booking.type)} />
          <Detail label="السعر" value={formatCurrency(booking.finalPrice)} />
          <Detail label="مصدر الحجز" value={booking.bookedBy === 'admin' ? 'الإدارة' : 'المريض'} />
          {booking.manualPatientCode && (
            <Detail label="كود المريض" value={booking.manualPatientCode} />
          )}
          {booking.voucherCode && <Detail label="كود الخصم" value={booking.voucherCode} />}
        </dl>

        {deposit !== 'none' && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="flex items-center gap-2 text-sm font-bold text-amber-200">
                <Icon name="wallet" size={15} />
                العربون — {DEPOSIT_STATE_LABELS[deposit]}
              </h4>
              <span className="text-sm font-bold text-amber-200">
                {formatCurrency(booking.depositAmount)}
                {booking.depositPercent > 0 && (
                  <span className="mr-1 text-[11px] font-normal text-amber-300/70">
                    ({booking.depositPercent}%)
                  </span>
                )}
              </span>
            </div>

            {booking.instaPayAccount && (
              <p className="mb-2 text-[11px] text-amber-200/80">
                حساب InstaPay: <span dir="ltr" className="font-mono">{booking.instaPayAccount}</span>
              </p>
            )}

            {booking.depositRejectionReason && (
              <p className="mb-2 text-[11px] text-red-300">
                سبب الرفض السابق: {booking.depositRejectionReason}
              </p>
            )}

            {booking.depositReceipt && (
              <>
                <Button variant="subtle" icon="eye" onClick={() => setShowReceipt((open) => !open)}>
                  {showReceipt ? 'إخفاء الإيصال' : 'عرض الإيصال'}
                </Button>
                {showReceipt && (
                  <img
                    src={booking.depositReceipt}
                    alt="إيصال العربون"
                    className="mt-2 max-h-64 w-full rounded-lg border border-white/10 object-contain"
                  />
                )}
              </>
            )}

            {deposit === 'awaiting_review' && (
              <div className="mt-3 flex gap-2">
                <Button variant="success" icon="check" onClick={onConfirmDeposit} loading={busy}>
                  تأكيد العربون
                </Button>
                <Button variant="danger" icon="x" onClick={onRejectDeposit}>
                  رفض الإيصال
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          {booking.status === BOOKING_STATUS.PENDING && (
            <Button variant="success" icon="check" onClick={onConfirm} loading={busy}>
              تأكيد الحجز
            </Button>
          )}
          {booking.status !== BOOKING_STATUS.COMPLETED && (
            <Button icon="check-circle-2" onClick={onComplete} loading={busy}>
              إنهاء الكشف
            </Button>
          )}
          <Button variant="ghost" icon="move" onClick={onMove}>
            نقل لموعد آخر
          </Button>
          <Button variant="danger" icon="x" onClick={onCancel}>
            إلغاء الحجز
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Detail({ label, value }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-slate-400">{label}</dt>
      <dd className="truncate text-xs font-bold text-slate-200">{value}</dd>
    </div>
  );
}

/* ========================================================================== */

function MoveBookingModal({ booking, slots, onClose, onMove }) {
  const [target, setTarget] = useState('');

  if (!booking) return null;

  const available = slots.filter((slot) => slot.available && slot.time !== booking.time);

  return (
    <Modal
      open
      onClose={onClose}
      title="نقل الحجز"
      icon="move"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button icon="check" disabled={!target} onClick={() => onMove(target)}>
            نقل
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-300">
          نقل حجز <strong className="text-white">{booking.patientName}</strong> من{' '}
          {formatTime12(booking.time)} إلى:
        </p>

        {available.length === 0 ? (
          <EmptyState icon="calendar-x" title="لا توجد مواعيد متاحة في هذا اليوم" />
        ) : (
          <Field label="الموعد الجديد">
            <Select value={target} onChange={(event) => setTarget(event.target.value)}>
              <option value="">اختر موعداً</option>
              {available.map((slot) => (
                <option key={slot.time} value={slot.time}>
                  {formatTime12(slot.time)}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <p className="rounded-lg bg-white/5 p-2.5 text-[11px] leading-relaxed text-slate-400">
          يحتفظ الحجز بسعره وخصوماته الأصلية بعد النقل.
        </p>
      </div>
    </Modal>
  );
}

/* ========================================================================== */

function RejectDepositModal({ booking, onClose, onSubmit }) {
  const [reason, setReason] = useState('');

  if (!booking) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title="رفض إيصال العربون"
      icon="x-circle"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button variant="danger" icon="x" disabled={!reason.trim()} onClick={() => onSubmit(reason.trim())}>
            رفض وإبلاغ المريض
          </Button>
        </div>
      }
    >
      <Field label="سبب الرفض" required hint="سيظهر هذا السبب للمريض ليعيد رفع إيصال صحيح">
        <Textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={4}
          placeholder="مثال: الإيصال غير واضح / المبلغ غير مطابق"
        />
      </Field>
    </Modal>
  );
}

/* ========================================================================== */

function ManualBookingModal({
  open,
  onClose,
  doctor,
  clinic,
  dateKey,
  slots,
  presetSlot,
  findPatient,
  onCreated,
  onError,
}) {
  const [form, setForm] = useState({
    patientName: '',
    patientPhone: '',
    patientCode: '',
    time: presetSlot || '',
    bookingType: BOOKING_TYPES.EXAM,
  });
  const [submitting, setSubmitting] = useState(false);

  // Reset whenever the modal is (re)opened for a different slot.
  useEffect(() => {
    if (open) {
      setForm((current) => ({ ...current, time: presetSlot || '' }));
    }
  }, [open, presetSlot]);

  if (!open) return null;

  const update = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const quote = quoteBooking({ doctor, clinic, bookingType: form.bookingType });
  const available = slots.filter((slot) => slot.available || slot.time === presetSlot);

  const handleSubmit = async () => {
    if (!form.patientName.trim() || !form.time) return;

    setSubmitting(true);
    try {
      // A code that matches a known patient links the booking to their account,
      // so it appears in their app rather than being an orphaned walk-in.
      const linked = form.patientCode ? findPatient(form.patientCode) : null;

      const payload = buildBookingPayload({
        doctor,
        clinic,
        patient: linked
          ? { id: linked.id, name: linked.name, phone: linked.phone }
          : { id: null, name: form.patientName.trim(), phone: form.patientPhone.trim() },
        dateKey,
        time: form.time,
        bookingType: form.bookingType,
        quote,
        bookedBy: 'doctor',
        source: 'manual',
        manualPatientCode: form.patientCode || null,
        manualPatientName: form.patientName.trim(),
      });

      await bookingApi.create(payload);
      onCreated();
      setForm({
        patientName: '',
        patientPhone: '',
        patientCode: '',
        time: '',
        bookingType: BOOKING_TYPES.EXAM,
      });
    } catch (err) {
      onError(
        err.status === 409
          ? 'هذا الموعد محجوز بالكامل. اختر موعداً آخر.'
          : err.message || 'تعذر إنشاء الحجز',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="حجز يدوي"
      icon="user-plus"
      size="md"
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-bold text-emerald-400">
            {formatCurrency(quote.finalPrice)}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              إلغاء
            </Button>
            <Button
              icon="check"
              loading={submitting}
              disabled={!form.patientName.trim() || !form.time}
              onClick={handleSubmit}
            >
              إنشاء الحجز
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="rounded-lg bg-white/5 p-2.5 text-xs text-slate-400">
          {clinic?.name} — {arabicDayOf(dateKey)} {isoToDisplayDate(dateKey)}
        </p>

        <Field label="اسم المريض" required>
          <Input value={form.patientName} onChange={update('patientName')} placeholder="الاسم" />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="رقم الهاتف">
            <Input value={form.patientPhone} onChange={update('patientPhone')} dir="ltr" placeholder="01xxxxxxxxx" />
          </Field>

          <Field label="كود المريض" hint="اختياري — لربط الحجز بحساب موجود">
            <Input value={form.patientCode} onChange={update('patientCode')} dir="ltr" />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="الموعد" required>
            <Select value={form.time} onChange={update('time')}>
              <option value="">اختر الموعد</option>
              {available.map((slot) => (
                <option key={slot.time} value={slot.time}>
                  {formatTime12(slot.time)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="نوع الزيارة">
            <Select value={form.bookingType} onChange={update('bookingType')}>
              <option value={BOOKING_TYPES.EXAM}>كشف</option>
              <option value={BOOKING_TYPES.FOLLOWUP}>متابعة</option>
            </Select>
          </Field>
        </div>
      </div>
    </Modal>
  );
}

export default DoctorSchedule;
